import { client } from '@api/client';
import { URL } from '@api/urls';

import { IConfigurationTemplate, ITreeParsed } from '@interfaces/cmModule/configurationTemplates';
import {
	IConfigurationTemplateDatesDTO,
	IConfigurationTemplateDTO,
	IConfigurationTemplateNetworkElementsDTO,
	IConfigurationTemplateSaveDTO,
	IConfigurationTemplateTimestampsDTO,
	TConfigurationTemplateUpdateNameDTO,
	TConfigurationTemplateUpdateNotesDTO,
	TConfigurationTemplateUpdateTreeDTO,
	TConfigurationTemplateValidationDTO,
	TDumpIdPath,
} from '@interfaces/cmModule/dto/configurationTemplatesDTO';
import {
	IAvailableDates,
	IAvailableTimestamps,
	IId,
	IName,
	TIdName,
	TNameTitle,
} from '@interfaces/common/common';
import { IMoTreeResponse } from '@interfaces/components/tree';
import { IValidationRuleFolderDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { IFlatTreeValidationRule } from '@components/common/FlatTree/interface';

import { IDumpId } from '../interfaces/cmModule/dto/cmModuleDTO';
import { IItemsResponse } from '../interfaces/common/response';

export const getConfigurationTemplates = (
	params: IConfigurationTemplateDTO,
): Promise<IItemsResponse<IConfigurationTemplate>> => {
	return client.get(URL.configurationTemplates, { params });
};

export const getConfigurationTemplate = ({ id }: IId): Promise<IConfigurationTemplate> => {
	return client.get(`${URL.configurationTemplates}/${id}`);
};

export const checkConfigurationTemplateName = (dto: IName): Promise<void> => {
	return client.post(`${URL.configurationTemplates}/check-name`, dto);
};

export const getConfigurationTemplateConfigTypes = (): Promise<TNameTitle[]> => {
	return client.get(`${URL.configurationTemplates}/config-types`);
};

export const getConfigurationTemplateNetworkElements = (
	dto: IConfigurationTemplateNetworkElementsDTO,
): Promise<TIdName[]> => {
	return client.post(`${URL.configurationTemplates}/network-elements`, dto);
};

export const saveConfigurationTemplate = (
	dto: IConfigurationTemplateSaveDTO,
): Promise<void> => {
	return client.post(`${URL.configurationTemplates}/set-template`, dto);
};

export const getConfigurationTemplateConfigDates = (
	dto: IConfigurationTemplateDatesDTO,
): Promise<IAvailableDates> => {
	return client.post(`${URL.configurationTemplates}/config-dates`, dto);
};

export const getConfigurationTemplateConfigTimestamps = (
	dto: IConfigurationTemplateTimestampsDTO<string>,
): Promise<IAvailableTimestamps[]> => {
	return client.post(`${URL.configurationTemplates}/config-date-with-all-timestamps`, dto);
};

export const getConfigurationTemplateConfigTree = (dto: TDumpIdPath): Promise<IMoTreeResponse> => {
	return client.post(`${URL.configurationTemplates}/get-config-tree`, dto);
};

export const runConfigurationTemplateConfigTree = (
	dto: IConfigurationTemplateTimestampsDTO,
): Promise<IDumpId> => {
	return client.post(`${URL.configurationTemplates}/run-config-tree`, dto);
};

export const checkConfigurationTemplateConfigTree = (
	params: IDumpId,
):Promise<ITreeParsed> => {
	return client.get(`${URL.configurationTemplates}/check-tree-parsed`, { params });
};

export const updateConfigurationTemplateConfigTree = (
	{ id, ...dto }: TConfigurationTemplateUpdateTreeDTO,
): Promise<void> => {
	return client.put(`${URL.configurationTemplates}/${id}/update-tree`, dto);
};

export const updateConfigurationTemplateNotes = ({ id, ...dto }: TConfigurationTemplateUpdateNotesDTO,
): Promise<void> => {
	return client.put(`${URL.configurationTemplates}/${id}/update-notes`, dto);
};

export const updateConfigurationTemplateName = ({ id, ...dto }: TConfigurationTemplateUpdateNameDTO):Promise<void> => {
	return client.put(`${URL.configurationTemplates}/${id}/update-name`, dto);
};

export const deleteConfigurationTemplate = ({ id }: IId):Promise<void> => {
	return client.post(`${URL.configurationTemplates}/${id}/delete`, {});
};

export const getConfigurationTemplateValidationFolders = ():Promise<IValidationRuleFolderDTO[]> => {
	return client.get(`${URL.configurationTemplates}/validation-rules-folders`);
};

export const getConfigurationTemplateValidationRules = (
	dto: TConfigurationTemplateValidationDTO,
):Promise<IFlatTreeValidationRule[]> => {
	return client.post(`${URL.configurationTemplates}/validation-rules`, dto);
};

export const getConfigurationTemplateUsers = ():Promise<TIdName[]> => {
	return client.get(`${URL.configurationTemplates}/users`);
};

export const getConfigurationTemplateModelCollections = ():Promise<TIdName[]> => {
	return client.get(`${URL.configurationTemplates}/yang-model-collections`);
};

export const getConfigurationTemplateAssignedStatuses = ():Promise<TNameTitle[]> => {
	return client.get(`${URL.configurationTemplates}/static/assigned-statuses`);
};

export const getConfigurationTemplateConfigTypesOptions = ():Promise<TNameTitle[]> => {
	return client.get(`${URL.configurationTemplates}/static/config-types`);
};
